import { useEffect, useRef, useState } from 'react'
import { Volume2, Download } from 'lucide-react'
import { Loading } from '../../kit'

/**
 * The "ฟังเสียง" control — a lazy call-recording player, shared by /calls
 * (CallList.jsx) and the patient chart's phone log (PhoneLogTable.jsx). Nothing
 * is fetched until it is clicked, and the Yalecom key stays server-side because
 * the bytes come through /api/call-recording.
 *
 * 🔑 WHY IT FETCHES A BLOB INSTEAD OF POINTING <audio src> AT THE ROUTE
 * ---------------------------------------------------------------------
 * /api/call-recording used to buffer the whole mp3 inside the Cloudflare Pages
 * Worker so it could answer with a real Content-Length: Yalecom sends the file
 * chunked with no length, and a length-less body that advertises Accept-Ranges
 * makes <audio> stall on its range probe. That buffer lived in the 128 MB isolate
 * EVERY route shares, uncapped, on a control the call centre clicks all day —
 * the same memory pressure that has served Cloudflare Error 1102 to unrelated
 * page loads (functions/lib/uploadGuard.js carries the measurements).
 *
 * So the route streams now, and the buffering moved HERE, into the one tab that
 * actually wants the bytes. `fetch()` has no opinion about Content-Length or byte
 * ranges — it just reads the stream to the end — and the `blob:` URL it produces
 * is a complete, local, fully seekable resource, which is exactly what the media
 * element wanted in the first place. Nothing regresses for the listener: the old
 * route withheld every byte until it had the whole file, so playback never
 * started early anyway, and now the Yalecom download and the browser download
 * overlap instead of running one after the other.
 *
 * ⚠️ Do not "simplify" this back to <audio src={api}>. That is the bug the
 * server-side buffer existed to fix, and the buffer is gone.
 *
 * ?dl=1 stays a plain <a href> — a download has no media element in front of it,
 * so it streams straight from the Worker to disk and never needs any of this.
 * (It carries the session cookie like any other same-origin navigation, which is
 * all /api/call-recording's auth gate asks for.)
 */
export default function CallRecordingPlayer({ uuid, className = '' }) {
  const [state, setState] = useState('idle')   // 'idle' | 'loading' | 'open' | 'error'
  const [src, setSrc] = useState('')
  // An un-revoked object URL keeps the whole recording in memory, and a busy
  // /calls screen can have several players open at once.
  const objectUrl = useRef('')
  // The in-flight download. Also this component's ONLY liveness flag: see load().
  const abort = useRef(null)

  // Unmount cleanup. There is deliberately no setup body — everything this
  // component owns is created by load(), on click.
  //
  // ⚠️ Under React.StrictMode (src/main.jsx, dev only) this runs
  // setup → cleanup → setup on mount, so the cleanup must not latch anything
  // that outlives it. It used to set a `gone.current = true` ref that nothing
  // ever reset: the dev double-invoke pinned it true for the life of every
  // player, so load() revoked its own perfectly healthy blob and returned
  // without ever leaving 'loading' — a permanent "กำลังโหลด…" with no error and
  // no way back, in the one environment where this component gets tested by
  // hand before it ships. The fix was to delete the flag: load() reads
  // `ctl.signal.aborted` instead, and a controller created inside load() cannot
  // survive into the next download the way a component-level ref can.
  //
  // ⚠️ Keep the deps empty. If this effect ever re-runs on a LIVE component,
  // the cleanup below aborts an in-flight download whose continuation then bails
  // out silently — same stuck "กำลังโหลด…", different cause. Anything that needs
  // deps has to reset state back to 'idle' in the setup body too.
  useEffect(() => () => {
    abort.current?.abort()
    if (objectUrl.current) URL.revokeObjectURL(objectUrl.current)
  }, [])

  if (!uuid) return null
  const api = `/api/call-recording?uuid=${encodeURIComponent(uuid)}`

  async function load() {
    // `state` here is a closure from the render that drew this button, so two
    // clicks dispatched before React re-renders would both read 'idle'. The ref
    // is what actually holds the line: a second download would overwrite
    // objectUrl.current and orphan the first blob, leaving a whole recording in
    // memory for the life of the tab with nothing left to revoke it. This
    // component downloads at most once per mount — the button only exists in
    // 'idle', and no state returns there — so a future retry-on-error path has
    // to null this out before it can call load() again.
    if (abort.current) return
    setState('loading')
    const ctl = new AbortController()
    abort.current = ctl
    try {
      // Deliberately raw fetch, NOT apiGet: this is a multi-megabyte download
      // that can run for seconds, and lib/api.js's read gate only has six slots
      // app-wide — parking one of them on an mp3 would stall the page's own
      // reads behind it. It is user-initiated and one at a time, which is the
      // same reason writes are not gated there either.
      const res = await fetch(api, { signal: ctl.signal })
      // The route answers 404 + JSON when Yalecom has no file for this call, so
      // "no recording" is distinguishable from "playback broke" — something an
      // <audio onError> handler could never tell apart.
      if (!res.ok) throw new Error(`recording_${res.status}`)
      const blob = await res.blob()
      // Unmounted mid-download: the cleanup above aborted THIS controller, so
      // the signal is the single source of truth for "this player is gone" —
      // nothing survives past one download, so nothing can latch.
      if (ctl.signal.aborted) return
      // A cut stream (the route's size ceiling errors the body rather than
      // truncating it) or an empty body would otherwise become a silent player
      // that never plays.
      if (!blob.size) throw new Error('recording_empty')
      const made = URL.createObjectURL(blob)
      // Nothing can unmount us between the check above and the hand-off below —
      // there is no await in between, and a React commit cannot interleave with
      // synchronous statements. The guard stays anyway because it is the line
      // that makes the object URL safe: if any future edit puts an await above
      // it, the cleanup will already have run and revoked nothing, and this blob
      // would hold the entire recording until the tab closes.
      if (ctl.signal.aborted) { URL.revokeObjectURL(made); return }
      objectUrl.current = made
      setSrc(made)
      setState('open')
    } catch {
      // An abort is an unmount, never a failure — and setState on an unmounted
      // component is exactly the noise this check exists to avoid.
      if (ctl.signal.aborted) return
      setState('error')
    }
  }

  if (state === 'error') return <span className={`${className} text-slate-400`}>ไม่มีไฟล์เสียง</span>

  if (state === 'loading') {
    return (
      <span className={className}><Loading variant="inline" /></span>
    )
  }

  if (state === 'open') {
    return (
      <span className={`${className} flex items-center gap-1.5`}>
        {/* autoPlay still works: the fetch was started by a click, so the document
            has user activation by the time the blob arrives. If a browser refuses
            it anyway the controls are right there. */}
        <audio controls autoPlay src={src} onError={() => setState('error')} className="h-7 max-w-[190px]" />
        <a href={`${api}&dl=1`} title="ดาวน์โหลดไฟล์เสียง" className="flex-none text-slate-400 hover:text-brand-600">
          <Download size={14} />
        </a>
      </span>
    )
  }

  return (
    <button type="button" onClick={load}
      className={`${className} inline-flex items-center gap-1 text-slate-400 hover:text-brand-600`}>
      <Volume2 size={13} /> ฟังเสียง
    </button>
  )
}
